import * as React from 'react'
import * as SheetPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'

import { faXmark } from '@document-crunch/pro-regular-svg-icons'

import { cn } from '@dc-shared-web/lib'
import { Icon } from '@dc-shared-web/components/base/Icon'
import { Button } from '@dc-shared-web/components/shadcn/button'

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = ({ ...props }: SheetPrimitive.DialogPortalProps) => (
  <SheetPrimitive.Portal {...props} />
)
SheetPortal.displayName = SheetPrimitive.Portal.displayName

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      'tw-fixed tw-inset-0 tw-z-50 tw-bg-background/80 tw-backdrop-blur-sm data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0',
      className
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  'tw-fixed tw-z-50 tw-gap-4 tw-bg-background tw-p-6 tw-shadow-lg tw-transition tw-ease-in-out data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-duration-300 data-[state=open]:tw-duration-500',
  {
    variants: {
      side: {
        top: 'tw-inset-x-0 tw-top-0 tw-border-b data-[state=closed]:tw-slide-out-to-top data-[state=open]:tw-slide-in-from-top',
        bottom:
          'tw-inset-x-0 tw-bottom-0 tw-border-t data-[state=closed]:tw-slide-out-to-bottom data-[state=open]:tw-slide-in-from-bottom',
        left: 'tw-inset-y-0 tw-left-0 h-full tw-border-r data-[state=closed]:tw-slide-out-to-left data-[state=open]:tw-slide-in-from-left',
        right:
          'tw-inset-y-0 tw-right-0 tw-h-full tw-border-l data-[state=closed]:tw-slide-out-to-right data-[state=open]:tw-slide-in-from-right',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  }
)

type SheetContentProps = React.ComponentPropsWithoutRef<
  typeof SheetPrimitive.Content
> &
  VariantProps<typeof sheetVariants> & {
    showOverlay?: boolean
    usePortal?: boolean
    allowOutsideClick?: boolean
  }

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = 'right',
      className,
      showOverlay,
      usePortal = true,
      allowOutsideClick,
      children,
      ...props
    },
    ref
  ) => {
    // the types PointerDownOutsideEvent | FocusOutsideEvent are not exported from radix, so we have to use any here
    const handleClickOutside = (e: any) => {
      if (
        (e.currentTarget as HTMLElement)?.getAttribute(
          'data-sheet-clickable'
        ) ||
        allowOutsideClick
      ) {
        e.preventDefault()
      }
    }
    return (
      <>
        {usePortal ? (
          <SheetPortal>
            <div className='dc-shared-web'>
              {showOverlay && <SheetOverlay />}
              <SheetPrimitive.Content
                ref={ref}
                className={cn(sheetVariants({ side }), className)}
                onInteractOutside={handleClickOutside}
                {...props}
              >
                {children}
                <SheetPrimitive.Close
                  className='tw-absolute tw-right-4 tw-top-4 tw-rounded-sm tw-opacity-70 tw-ring-offset-background tw-transition-opacity hover:tw-opacity-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2 disabled:tw-pointer-events-none data-[state=open]:tw-bg-secondary'
                  data-testid='close-button-xmark'
                >
                  <Icon icon={faXmark} size='xl' />
                  <span className='tw-sr-only'>Close</span>
                </SheetPrimitive.Close>
              </SheetPrimitive.Content>
            </div>
          </SheetPortal>
        ) : (
          <div className='dc-shared-web'>
            {showOverlay && <SheetOverlay />}
            <SheetPrimitive.Content
              ref={ref}
              className={cn(sheetVariants({ side }), className)}
              onInteractOutside={handleClickOutside}
              {...props}
            >
              {children}
              <SheetPrimitive.Close asChild>
                <Button
                  className='tw-absolute tw-right-[4px] tw-top-[9px] tw-rounded-md tw-ring-offset-background tw-transition-opacity hover:tw-opacity-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2 disabled:tw-pointer-events-none data-[state=open]:tw-bg-secondary'
                  data-testid={`team-sheet-close-btn`}
                  variant='ghost'
                  size={'icon'}
                >
                  <Icon
                    icon={faXmark}
                    size='2xl'
                    className='tw-text-neutral-400'
                  />
                </Button>
              </SheetPrimitive.Close>
            </SheetPrimitive.Content>
          </div>
        )}
      </>
    )
  }
)
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('tw-flex tw-flex-col tw-space-y-2 tw-text-left', className)}
    {...props}
  />
)
SheetHeader.displayName = 'SheetHeader'

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'tw-flex tw-flex-col-reverse sm:tw-flex-row sm:tw-justify-end sm:tw-space-x-2',
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = 'SheetFooter'

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('tw-text-xl tw-font-medium tw-text-neutral-700', className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('tw-text-sm tw-text-muted-foreground', className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
